import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { Divider, Stack, Typography } from "@mui/material";
import moment from "moment";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import DoneOutlinedIcon from "@mui/icons-material/DoneOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import { useCustomQuery } from "store/hooks/react-query/query/useQuery";
import {
  GET_ALL_DEPARTMENT_INVENTORY,
  GET_ALL_STOCKS,
  GET_REQUEST_HISTORY,
} from "utils/reactQueryKeys";
import { useQueryClient } from "react-query";
import SearchBar from "components/atoms/SearchBar";
import CustomModal from "components/atoms/CustomModal";
// import StatusUpdateModal from "./StatusUpdateModal";
import CustomSelect from "components/atoms/Select";
import CustomButton from "components/atoms/CustomButton";
import { useCustomMutation } from "store/hooks/react-query/mutate/useMutateFunc";
import { toast } from "react-toastify";
import Pagination from "components/molecules/pagination/Pagination";

const fullName = (user) =>
  user?.fullName ||
  `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
  "N/A";

function Row({ row, toggleModal }) {
  let status;
  if (row.approval === "APPROVED") {
    status = (
      <Stack
        direction="row"
        spacing={1}
        sx={{ color: "blue" }}
        alignItems="center"
      >
        <CheckCircleOutlinedIcon sx={{ fontSize: "18px" }} />
        {row.approval}
      </Stack>
    );
  } else if (row.approval === "FULFILLED") {
    status = (
      <Stack direction="row" spacing={1} sx={{ color: "primary.success" }}>
        <DoneOutlinedIcon sx={{ fontSize: "18px" }} />
        {row.approval}
      </Stack>
    );
  } else if (row.approval === "REJECTED") {
    status = (
      <Stack direction="row" spacing={1} sx={{ color: "primary.error" }}>
        <CloseOutlinedIcon sx={{ fontSize: "18px" }} />
        {row.approval}
      </Stack>
    );
  } else {
    status = (
      <Stack direction="row" spacing={1} sx={{ color: "secondary.main" }}>
        <CheckCircleOutlinedIcon sx={{ fontSize: "18px" }} />
        PENDING
      </Stack>
    );
  }

  // one table row per requested item; order-level cells span all of them
  const entries = row?.items?.length ? row.items : [null];
  const span = entries.length;
  const cellSx = { color: "primary.darkGrey" };

  return (
    <React.Fragment>
      {entries.map((entry, idx) => (
        <TableRow
          key={entry?.batchId || entry?._id || idx}
          sx={{
            "& td": {
              borderBottom:
                idx === span - 1 ? "1px solid rgba(224, 224, 224, 1)" : "unset",
            },
          }}
        >
          {idx === 0 && (
            <TableCell align="left" rowSpan={span} sx={cellSx}>
              {row?.createdAt
                ? moment(row.createdAt).format("DD/MM/YYYY hh:mm A")
                : "N/A"}
            </TableCell>
          )}
          {idx === 0 && (
            <TableCell align="left" rowSpan={span} sx={cellSx}>
              {row?.uniqueCode}
            </TableCell>
          )}

          <TableCell align="left" sx={cellSx}>
            {entry?.item?.uniqueCode || "N/A"}
          </TableCell>
          <TableCell align="left" sx={cellSx}>
            {entry?.item?.itemName || "N/A"}
          </TableCell>
          <TableCell align="left" sx={cellSx}>
            {entry?.item?.unitType || "N/A"}
          </TableCell>
          <TableCell align="left" sx={cellSx}>
            {entry?.quantity ?? "N/A"}
          </TableCell>

          {idx === 0 && (
            <TableCell
              align="left"
              rowSpan={span}
              sx={{ ...cellSx, textTransform: "capitalize" }}
            >
              {fullName(row?.createdBy)}
            </TableCell>
          )}
          {idx === 0 && (
            <TableCell
              align="left"
              rowSpan={span}
              sx={{ ...cellSx, textTransform: "capitalize" }}
            >
              {row?.approvedOrRejectedBy
                ? fullName(row.approvedOrRejectedBy)
                : "-"}
            </TableCell>
          )}
          {idx === 0 && (
            <TableCell align="left" rowSpan={span} sx={cellSx}>
              {status}
            </TableCell>
          )}
          {idx === 0 && (
            <TableCell align="left" rowSpan={span} sx={cellSx}>
              <CustomButton
                text="View"
                color="secondary"
                onClick={() => toggleModal(row)}
                variant="text"
              />
            </TableCell>
          )}
        </TableRow>
      ))}
    </React.Fragment>
  );
}

const ModalContent = ({ selectedRow, closeModal }) => {
  const queryClient = useQueryClient();

  const { mutate: updateStatus, isLoading } = useCustomMutation(
    {
      url: `/lab-stock/fulfill-approved-order/${selectedRow?.id}`,
      method: "patch",
      data: {
        approval: "FULFILLED",
      },
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(GET_REQUEST_HISTORY);
        queryClient.invalidateQueries(GET_ALL_STOCKS);
        queryClient.invalidateQueries(GET_ALL_DEPARTMENT_INVENTORY);

        toast.success("Success");
        closeModal();
      },
      onError: (error) => {
        if (Array.isArray(error.message)) {
          return error.message.map((msg) => toast.error(msg));
        }
        return toast.error(error.message);
      },
    }
  );

  return (
    <Stack
      direction={"column"}
      sx={{ width: "100%", overflowY: "hidden", height: "auto" }}
      spacing={3}
    >
      <Stack
        direction={"column"}
        sx={{ width: "100%", overflowY: "hidden" }}
        spacing={1}
      >
        <Typography variant="displayMd">Status Update</Typography>
      </Stack>
      <Divider />
      <Stack direction={"row"} alignItems="center" spacing={1}>
        <Typography variant="h6">Requested by: </Typography>{" "}
        <Typography fontSize={"1rem"} textTransform="capitalize">
          {" "}
          {`${selectedRow?.createdBy?.fullName}`}
        </Typography>
      </Stack>
      {selectedRow?.approval === "REJECTED" ? (
        <Stack direction={"row"} alignItems="center" spacing={1}>
          <Typography variant="h6">Reason for decline: </Typography>{" "}
          <Typography fontSize={"1rem"} textTransform="capitalize">
            {" "}
            {`${selectedRow?.reasonForDecline}`}
          </Typography>
        </Stack>
      ) : null}
      <Stack
        direction={"column"}
        sx={{
          width: "100%",
          height: "45vh",

          overflow: "auto",
        }}
        spacing={3}
      >
        {selectedRow?.items?.map((item, i) => (
          <Stack
            xs={12}
            sm={8}
            key={i}
            sx={{ backgroundColor: "background.custom", p: 3 }}
          >
            <Stack direction={"column"} sx={{ width: "100%" }} spacing={1}>
              <Typography variant="heading">
                {`${item?.item?.itemName || ""}`}
              </Typography>
              <Stack>
                <Typography variant="p">
                  <Typography variant="p" fontWeight={"bold"}>
                    Quanity:{" "}
                  </Typography>{" "}
                  {`${item?.quantity}`} pieces
                </Typography>

                <Typography>
                  <Typography variant="p" fontWeight={"bold"}>
                    Note:{" "}
                  </Typography>
                  {`${item?.note || ""}`}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        ))}
      </Stack>
      <Stack direction={"row"} xs={6} sm={3} spacing={1}>
        <CustomButton
          text="Fulfill"
          color="secondary"
          disabled={
            isLoading ||
            selectedRow?.approval === "FULFILLED" ||
            selectedRow?.approval === "REJECTED" ||
            selectedRow?.approval === "DECLINED"
          }
          onClick={() => {
            updateStatus();
          }}
        />
        <CustomButton
          variant="lightSecondary"
          text="Cancel"
          color="secondary"
          onClick={closeModal}
        />
      </Stack>
    </Stack>
  );
};

export default function RequestHistoryTable({
  data,
  currentPage,
  handlePageChange,
}) {
  const rows = data?.orders;

  const [selectedRow, setSelectedRow] = React.useState(null);

  const queryClient = useQueryClient();
  const modalRef = React.useRef(null);
  const [search, setSearch] = React.useState("");

  const { isLoading: searchLoad, refetch: refetchSearch } = useCustomQuery(
    [GET_REQUEST_HISTORY, currentPage, search],
    {
      url: `lab-stock/order-history`,
      method: "post",
      data: {
        search,
        limit: 10,
        page: currentPage,
      },
    },
    {
      refetchOnWindowFocus: true,
      onSuccess: (response) => {
        queryClient.setQueryData(
          [GET_REQUEST_HISTORY, currentPage],
          (oldQueryData) => {
            return {
              ...response,
            };
          }
        );
      },
      enabled: false,
    }
  );

  const toggleModal = (row) => {
    setSelectedRow(row);
    modalRef?.current?.handleToggle();
  };

  return (
    <Stack>
      <Stack
        direction={"row"}
        sx={{ width: "100%", justifyContent: "flex-end" }}
      >
        <SearchBar
          refetch={refetchSearch}
          search={search}
          isLoading={searchLoad}
          setsearch={setSearch}
          placeholder="Search by requester, item or code"
        />
      </Stack>
      {rows?.length ? (
        <>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 1100 }} aria-label="request-history-table">
              <TableHead>
                <TableRow>
                  {[
                    "Date",
                    "Request Code",
                    "Item No.",
                    "Item",
                    "Unit",
                    "Quantity",
                    "Requested By",
                    "Reviewed By",
                    "Status",
                    "",
                  ].map((h, i) => (
                    <TableCell
                      key={i}
                      align="left"
                      sx={{ fontWeight: "bold" }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, i) => (
                  <Row key={row?.id || row?._id || i} row={row} toggleModal={toggleModal} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack sx={{ p: { xs: 0, sm: 2 }, width: "100%" }}>
            <Pagination
              currentPage={currentPage}
              totalCount={data?.count || 5}
              pageSize={10}
              onPageChange={handlePageChange}
            />
          </Stack>
        </>
      ) : (
        "No Data Found"
      )}
      <CustomModal
        ref={modalRef}
        childrenContSx={{
          p: 4,
          // height: "100vh !important",

          width: {
            xs: "100%",
            sm: "50vw",
          },
        }}
      >
        <ModalContent
          selectedRow={selectedRow}
          closeModal={() => modalRef?.current?.handleToggle()}
        />
      </CustomModal>
    </Stack>
  );
}